import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Phone,
  FileText,
  MapPin,
  Save,
  CheckCircle2,
  Loader2,
  X,
  Plus,
  Trash2
} from 'lucide-react';
import { createDistributorAPI, updateDistributorAPI, getDistributorByIdAPI } from '../../api/distributors';
import {
  getZonesAPI,
  getCitiesAPI,
  getDeposAPI,
  getRoutesAPI,
  getTerritoriesAPI
} from '../../api/locations';
import { getDistributorChannelsList, type ApiDistributorChannel } from '../../api/distributorChannel';

export interface LocationOption {
  id: number | string;
  name: string;
}

export interface AddressItemState {
  id: string;
  backendId?: number;
  addressType: string;
  addressLine1: string;
  pincode: string;
  zoneId: string;
  territoryId: string;
  cityId: string;
  depoId: string;
  routeId: string;
  isDefault: boolean;
}

export const AddDistributor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);

  // Basic Form State matching backend API schema
  const [formData, setFormData] = useState({
    // Basic Details
    companyName: '',
    sapCode: '',
    loginId: '',
    password: '',
    distributorTypeId: '',
    creditLimit: '0',
    paymentTerms: 'NET30',
    status: 'ACTIVE',

    // Contact Details
    firstName: '',
    lastName: '',
    phone: '',
    alternatePhone: '',
    email: '',

    // Tax & Legal Details
    gst: '',
    panNo: '',
    fassiNo: '',
  });

  // Dynamic Multiple Addresses State
  const [addresses, setAddresses] = useState<AddressItemState[]>([
    {
      id: 'addr_1',
      addressType: 'BILLING',
      addressLine1: '',
      pincode: '',
      zoneId: '',
      territoryId: '',
      cityId: '',
      depoId: '',
      routeId: '',
      isDefault: true,
    },
  ]);

  // Dropdown Options State from Location & Channels APIs
  const [zones, setZones] = useState<LocationOption[]>([]);
  const [territories, setTerritories] = useState<LocationOption[]>([]);
  const [cities, setCities] = useState<LocationOption[]>([]);
  const [depos, setDepos] = useState<LocationOption[]>([]);
  const [routes, setRoutes] = useState<LocationOption[]>([]);
  const [channels, setChannels] = useState<ApiDistributorChannel[]>([]);

  // UX State
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch location dropdowns and channels
  useEffect(() => {
    let isMounted = true;
    const fetchDropdowns = async () => {
      try {
        const [zonesRes, terrRes, citiesRes, deposRes, routesRes, channelsRes] = await Promise.all([
          getZonesAPI().catch(() => null),
          getTerritoriesAPI().catch(() => null),
          getCitiesAPI().catch(() => null),
          getDeposAPI().catch(() => null),
          getRoutesAPI().catch(() => null),
          getDistributorChannelsList().catch(() => null),
        ]);

        if (!isMounted) return;

        const parseOptions = (res: any): LocationOption[] => {
          const list = res?.data || res?.zones || res?.cities || res?.depos || res?.routes || res?.territories || (Array.isArray(res) ? res : []);
          if (Array.isArray(list)) {
            return list.map((item: any) => ({
              id: item.id || item.code || item.name,
              name: item.name || item.cityName || item.zoneName || item.depoName || item.routeName || item.territoryName || String(item),
            }));
          }
          return [];
        };

        setZones(parseOptions(zonesRes));
        setTerritories(parseOptions(terrRes));
        setCities(parseOptions(citiesRes));
        setDepos(parseOptions(deposRes));
        setRoutes(parseOptions(routesRes));

        if (channelsRes) {
          const channelList: ApiDistributorChannel[] = channelsRes?.data || (Array.isArray(channelsRes) ? channelsRes : []);
          if (Array.isArray(channelList)) {
            setChannels(channelList);
            if (channelList.length > 0) {
              setFormData((prev) => ({
                ...prev,
                distributorTypeId: prev.distributorTypeId || String(channelList[0].id),
              }));
            }
          }
        }
      } catch (err) {
        console.warn('Error fetching location dropdowns:', err);
      }
    };
    fetchDropdowns();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch existing distributor details when in edit mode
  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    const fetchDistributorData = async () => {
      try {
        setInitialLoading(true);
        const res = await getDistributorByIdAPI(id);
        if (!isMounted) return;
        const data = res?.data || res?.distributor || res;
        if (data) {
          setFormData({
            companyName: data.companyName || data.name || '',
            sapCode: data.sapCode || data.code || '',
            loginId: data.loginId || data.sapCode || '',
            password: '',
            distributorTypeId: data.distributorTypeId ? String(data.distributorTypeId) : '',
            creditLimit: String(data.creditLimit || (data.walletBalances && data.walletBalances[0]?.creditLimit) || '0'),
            paymentTerms: data.paymentTerms || 'NET30',
            status: data.status || 'ACTIVE',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            alternatePhone: data.alternatePhone || '',
            email: data.email || '',
            gst: data.gst || '',
            panNo: data.panNo || data.pan || '',
            fassiNo: data.fassiNo || data.fssai || '',
          });

          if (Array.isArray(data.addresses) && data.addresses.length > 0) {
            setAddresses(
              data.addresses.map((addr: any, idx: number) => ({
                id: addr.id ? String(addr.id) : `addr_${idx + 1}`,
                backendId: typeof addr.id === 'number' ? addr.id : undefined,
                addressType: addr.addressType || (idx === 0 ? 'BILLING' : 'SHIPPING'),
                addressLine1: addr.addressLine1 || addr.address || '',
                pincode: addr.pincode || '',
                zoneId: addr.zoneId ? String(addr.zoneId) : (addr.zone?.id ? String(addr.zone.id) : ''),
                territoryId: addr.territoryId ? String(addr.territoryId) : (addr.territory?.id ? String(addr.territory.id) : ''),
                cityId: addr.cityId ? String(addr.cityId) : (addr.city?.id ? String(addr.city.id) : ''),
                depoId: addr.depoId ? String(addr.depoId) : (addr.depo?.id ? String(addr.depo.id) : ''),
                routeId: addr.routeId ? String(addr.routeId) : (addr.route?.id ? String(addr.route.id) : ''),
                isDefault: addr.isDefault !== undefined ? addr.isDefault : (idx === 0),
              }))
            );
          }
        }
      } catch (err: any) {
        console.error('Failed to load distributor details for edit:', err);
        setErrorMessage('Failed to load distributor details.');
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };
    fetchDistributorData();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // Address Handlers
  const handleAddAddress = () => {
    setAddresses((prev) => [
      ...prev,
      {
        id: `addr_${Date.now()}`,
        addressType: prev.length === 0 ? 'BILLING' : 'SHIPPING',
        addressLine1: '',
        pincode: '',
        zoneId: '',
        territoryId: '',
        cityId: '',
        depoId: '',
        routeId: '',
        isDefault: prev.length === 0,
      },
    ]);
  };

  const handleRemoveAddress = (id: string) => {
    if (addresses.length <= 1) return;
    setAddresses((prev) => prev.filter((item) => item.id !== id));
  };

  const handleAddressChange = (id: string, field: keyof AddressItemState, value: any) => {
    setAddresses((prev) =>
      prev.map((addr) => (addr.id === id ? { ...addr, [field]: value } : addr))
    );
    const errKey = `${id}_${field}`;
    if (errors[errKey]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[errKey];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company Name is required.';
    }

    if (!isEdit && !formData.sapCode.trim()) {
      newErrors.sapCode = 'SAP Code is required.';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Primary Phone Number is required.';
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Invalid email address.';
    }

    if (addresses.length > 0) {
      const primaryAddr = addresses[0];
      if (!primaryAddr.addressLine1.trim()) {
        newErrors[`${primaryAddr.id}_addressLine1`] = 'Street address is required.';
      }
      if (!primaryAddr.pincode.trim()) {
        newErrors[`${primaryAddr.id}_pincode`] = 'Pincode is required.';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setErrorMessage('Please fill in all required fields.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setErrors({});

    try {
      setLoading(true);

      const formattedAddresses = addresses.map((addr, idx) => ({
        ...(addr.backendId ? { id: addr.backendId } : (!isNaN(Number(addr.id)) ? { id: Number(addr.id) } : {})),
        addressType: addr.addressType || (idx === 0 ? 'BILLING' : 'SHIPPING'),
        addressLine1: addr.addressLine1.trim(),
        addressLine2: '',
        cityId: addr.cityId ? Number(addr.cityId) : undefined,
        routeId: addr.routeId ? Number(addr.routeId) : undefined,
        pincode: addr.pincode.trim(),
        isDefault: idx === 0 ? true : addr.isDefault,
      }));

      const firstAddress = formattedAddresses[0] || {};
      const selectedChannelId = formData.distributorTypeId ? Number(formData.distributorTypeId) : (channels[0]?.id || 10);

      const payload: any = {
        companyName: formData.companyName.trim(),
        firstName: formData.firstName.trim() || null,
        lastName: formData.lastName.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        alternatePhone: formData.alternatePhone.trim() || null,
        paymentTerms: formData.paymentTerms || 'NET30',
        gst: formData.gst.trim().toUpperCase() || null,
        panNo: formData.panNo.trim().toUpperCase() || null,
        fassiNo: formData.fassiNo.trim() || null,
        status: formData.status || 'ACTIVE',
        distributorTypeId: selectedChannelId,
        creditLimit: formData.creditLimit ? Number(formData.creditLimit) : 0,
        address: firstAddress,
        addresses: formattedAddresses,
      };

      if (!isEdit) {
        payload.sapCode = formData.sapCode.trim();
        payload.loginId = formData.loginId.trim() || formData.sapCode.trim();
        payload.password = formData.password || 'Password@123';
      } else if (formData.password.trim()) {
        payload.password = formData.password.trim();
      }

      if (isEdit && id) {
        await updateDistributorAPI(id, payload);
        setSuccessMessage('Distributor updated successfully!');
      } else {
        await createDistributorAPI(payload);
        setSuccessMessage('Distributor created successfully!');
      }

      setTimeout(() => {
        navigate('/distributor');
      }, 1200);
    } catch (err: any) {
      setErrorMessage(err?.message || `Failed to ${isEdit ? 'update' : 'create'} distributor. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <Loader2 className="w-8 h-8 text-[#C1272D] animate-spin" />
        <span className="text-xs font-semibold text-slate-500">Loading distributor details...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 select-none pb-12 animate-fadeIn">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/distributor')}
            className="w-9 h-9 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Back to Distributors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
              {isEdit ? 'Edit Distributor' : 'Add New Distributor'}
            </h1>
            <p className="text-xs text-slate-500 font-semibold mt-0.5">
              {isEdit
                ? `Update details for distributor ${formData.companyName || formData.sapCode || ''}`
                : 'Fill in the distributor details below to create a new partner account.'}
            </p>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold p-4 rounded-2xl flex items-center gap-3 shadow-xs animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold p-4 rounded-2xl flex items-center justify-between shadow-xs animate-fadeIn">
          <span>{errorMessage}</span>
          <button type="button" onClick={() => setErrorMessage(null)} className="text-rose-500 hover:text-rose-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Form Card Container */}
      <form id="add-distributor-form" onSubmit={handleSubmit} noValidate className="space-y-6">
        {/* Section 1: Basic Details */}
        <div className="bg-white rounded-3xl border border-[#ECECF2] p-6 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
            <div className="w-8 h-8 rounded-xl bg-amber-100/70 text-amber-700 flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider m-0">
              Basic Details
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-600">
                  Company Name <span className="text-rose-500">*</span>
                </label>
                {errors.companyName && (
                  <span className="text-rose-500 text-xs font-bold">
                    {errors.companyName}
                  </span>
                )}
              </div>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="e.g. Shree Ganesh Dairy & Sweets"
                className={`w-full bg-slate-50 border rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none transition-all ${
                  errors.companyName
                    ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                    : 'border-slate-200 focus:border-[#C1272D] focus:bg-white'
                }`}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-600">
                  SAP Code {!isEdit && <span className="text-rose-500">*</span>}
                </label>
                {errors.sapCode && (
                  <span className="text-rose-500 text-xs font-bold">
                    {errors.sapCode}
                  </span>
                )}
              </div>
              <input
                type="text"
                name="sapCode"
                value={formData.sapCode}
                onChange={handleChange}
                disabled={isEdit}
                readOnly={isEdit}
                placeholder="e.g. DIST1001"
                className={`w-full border rounded-xl px-3.5 py-2.5 text-xs font-semibold focus:outline-none transition-all ${
                  isEdit
                    ? 'bg-slate-200/60 border-slate-300 text-slate-500 cursor-not-allowed'
                    : errors.sapCode
                    ? 'bg-rose-50/20 border-rose-400 focus:border-rose-500 text-slate-800'
                    : 'bg-slate-50 border-slate-200 text-slate-800 focus:border-[#C1272D] focus:bg-white'
                }`}
              />
              {isEdit && (
                <span className="text-[10px] text-amber-700 font-bold mt-1 block">
                  ℹ️ SAP Code is permanent and cannot be modified.
                </span>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Login ID
              </label>
              <input
                type="text"
                name="loginId"
                value={formData.loginId}
                onChange={handleChange}
                placeholder="Defaults to SAP Code"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Password@123"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Distributor Type
              </label>
              <select
                name="distributorTypeId"
                value={formData.distributorTypeId}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all cursor-pointer"
              >
                {channels.length === 0 ? (
                  <option value="">Select Channel</option>
                ) : (
                  channels.map((chan) => (
                    <option key={chan.id} value={chan.id}>
                      {chan.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Payment Terms
              </label>
              <select
                name="paymentTerms"
                value={formData.paymentTerms}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all cursor-pointer"
              >
                <option value="NET30">NET30 (30 Days)</option>
                <option value="NET15">NET15 (15 Days)</option>
                <option value="NET7">NET7 (7 Days)</option>
                <option value="NET60">NET60 (60 Days)</option>
                <option value="ADVANCE">Advance Payment</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Credit Limit (₹)
              </label>
              <input
                type="number"
                name="creditLimit"
                value={formData.creditLimit}
                onChange={handleChange}
                placeholder="e.g. 50000"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all cursor-pointer"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section 2: Contact Details */}
        <div className="bg-white rounded-3xl border border-[#ECECF2] p-6 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
            <div className="w-8 h-8 rounded-xl bg-blue-100/70 text-blue-700 flex items-center justify-center">
              <Phone className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider m-0">
              Contact Details
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="e.g. Ramesh"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="e.g. Patil"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-600">
                  Primary Phone Number <span className="text-rose-500">*</span>
                </label>
                {errors.phone && (
                  <span className="text-rose-500 text-xs font-bold">
                    {errors.phone}
                  </span>
                )}
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g. 9876543210"
                className={`w-full bg-slate-50 border rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none transition-all ${
                  errors.phone
                    ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                    : 'border-slate-200 focus:border-[#C1272D] focus:bg-white'
                }`}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Alternate Phone Number
              </label>
              <input
                type="tel"
                name="alternatePhone"
                value={formData.alternatePhone}
                onChange={handleChange}
                placeholder="e.g. 9876543211"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-600">
                  Email Address
                </label>
                {errors.email && (
                  <span className="text-rose-500 text-xs font-bold">
                    {errors.email}
                  </span>
                )}
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="ramesh.patil@example.com"
                className={`w-full bg-slate-50 border rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none transition-all ${
                  errors.email
                    ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                    : 'border-slate-200 focus:border-[#C1272D] focus:bg-white'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Section 3: Tax & Legal Details */}
        <div className="bg-white rounded-3xl border border-[#ECECF2] p-6 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
            <div className="w-8 h-8 rounded-xl bg-purple-100/70 text-purple-700 flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider m-0">
              Tax & Registration Details
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                GST Number
              </label>
              <input
                type="text"
                name="gst"
                value={formData.gst}
                onChange={handleChange}
                placeholder="27ABCDE1234F1Z5"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold uppercase focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                PAN Number
              </label>
              <input
                type="text"
                name="panNo"
                value={formData.panNo}
                onChange={handleChange}
                placeholder="ABCDE1234F"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold uppercase focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                FSSAI License Number
              </label>
              <input
                type="text"
                name="fassiNo"
                value={formData.fassiNo}
                onChange={handleChange}
                placeholder="12345678901234"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
              />
            </div>
          </div>
        </div>

        {/* Section 4: Address & Location Details (Dynamic Multiple Addresses) */}
        <div className="bg-white rounded-3xl border border-[#ECECF2] p-6 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-100/70 text-emerald-700 flex items-center justify-center">
                <MapPin className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider m-0">
                Address & Location Details ({addresses.length})
              </h3>
            </div>

            <button
              type="button"
              onClick={handleAddAddress}
              className="px-3.5 py-1.5 bg-emerald-50 hover:bg-emerald-100/80 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Plus className="w-4 h-4 text-emerald-600" />
              <span>Add Another Address</span>
            </button>
          </div>

          <div className="space-y-6">
            {addresses.map((addrItem, idx) => (
              <div
                key={addrItem.id}
                className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 space-y-4 relative transition-all"
              >
                {/* Address Card Top Bar */}
                <div className="flex items-center justify-between border-b border-slate-200/60 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-white border border-slate-200 text-slate-700 text-xs font-extrabold rounded-full">
                      Address {idx + 1}
                    </span>
                    <select
                      value={addrItem.addressType}
                      onChange={(e) => handleAddressChange(addrItem.id, 'addressType', e.target.value)}
                      className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded-lg px-2.5 py-1 outline-none focus:border-slate-400 cursor-pointer"
                    >
                      <option value="BILLING">Billing Address</option>
                      <option value="SHIPPING">Shipping Address</option>
                      <option value="WAREHOUSE">Warehouse Address</option>
                    </select>
                  </div>

                  {addresses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveAddress(addrItem.id)}
                      className="text-slate-400 hover:text-rose-600 transition-colors p-1 rounded-lg hover:bg-rose-50 cursor-pointer flex items-center gap-1 text-xs font-semibold"
                      title="Remove Address"
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                      <span className="hidden sm:inline text-rose-600">Remove</span>
                    </button>
                  )}
                </div>

                {/* Single Address Input */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-slate-600">
                      Address {idx === 0 && <span className="text-rose-500">*</span>}
                    </label>
                    {errors[`${addrItem.id}_addressLine1`] && (
                      <span className="text-rose-500 text-xs font-bold">
                        {errors[`${addrItem.id}_addressLine1`]}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={addrItem.addressLine1}
                    onChange={(e) => handleAddressChange(addrItem.id, 'addressLine1', e.target.value)}
                    placeholder="Enter full street address (Building / Street / Area / Landmark)"
                    className={`w-full bg-white border rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none transition-all ${
                      errors[`${addrItem.id}_addressLine1`]
                        ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                        : 'border-slate-200 focus:border-[#C1272D]'
                    }`}
                  />
                </div>

                {/* Sequence: Pincode, Zone, Territory, City, Depo, Route */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold text-slate-600">
                        Pincode {idx === 0 && <span className="text-rose-500">*</span>}
                      </label>
                      {errors[`${addrItem.id}_pincode`] && (
                        <span className="text-rose-500 text-xs font-bold">
                          {errors[`${addrItem.id}_pincode`]}
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      value={addrItem.pincode}
                      onChange={(e) => handleAddressChange(addrItem.id, 'pincode', e.target.value)}
                      placeholder="e.g. 410206"
                      className={`w-full bg-white border rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none transition-all ${
                        errors[`${addrItem.id}_pincode`]
                          ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                          : 'border-slate-200 focus:border-[#C1272D]'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Zone
                    </label>
                    <select
                      value={addrItem.zoneId}
                      onChange={(e) => handleAddressChange(addrItem.id, 'zoneId', e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] transition-all cursor-pointer"
                    >
                      <option value="">Select Zone</option>
                      {zones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Territory
                    </label>
                    <select
                      value={addrItem.territoryId}
                      onChange={(e) => handleAddressChange(addrItem.id, 'territoryId', e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] transition-all cursor-pointer"
                    >
                      <option value="">Select Territory</option>
                      {territories.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      City
                    </label>
                    <select
                      value={addrItem.cityId}
                      onChange={(e) => handleAddressChange(addrItem.id, 'cityId', e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] transition-all cursor-pointer"
                    >
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Depo
                    </label>
                    <select
                      value={addrItem.depoId}
                      onChange={(e) => handleAddressChange(addrItem.id, 'depoId', e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] transition-all cursor-pointer"
                    >
                      <option value="">Select Depo</option>
                      {depos.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Route
                    </label>
                    <select
                      value={addrItem.routeId}
                      onChange={(e) => handleAddressChange(addrItem.id, 'routeId', e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] transition-all cursor-pointer"
                    >
                      <option value="">Select Route</option>
                      {routes.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Another Address Button Below */}
          <div className="pt-1 flex justify-start">
            <button
              type="button"
              onClick={handleAddAddress}
              className="px-4 py-2 bg-[#FFF8E1] hover:bg-[#FFF3C4] text-amber-900 border border-amber-300/80 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-2xs"
            >
              <Plus className="w-4 h-4 text-amber-700" />
              <span>+ Add Another Address</span>
            </button>
          </div>
        </div>

        {/* Bottom Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/distributor')}
            className="px-5 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{loading ? 'Saving...' : 'Save Distributor'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDistributor;
